import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { useEffect, useState, type ReactNode } from "react";

type NotificationSeverity = "error" | "info" | "success";

interface BusinessNotification {
  id: number;
  message: string;
  severity: NotificationSeverity;
}

type NotificationListener = (notification: BusinessNotification) => void;

const listeners = new Set<NotificationListener>();
let nextNotificationId = 1;

function publish(severity: NotificationSeverity, message: string): number {
  const notification = {
    id: nextNotificationId,
    message,
    severity,
  } satisfies BusinessNotification;
  nextNotificationId += 1;
  for (const listener of listeners) listener(notification);
  return notification.id;
}

/**
 * Imperative feedback seam for mutations that complete outside a form surface.
 * Presentation stays native MUI in `BusinessNotificationProvider`.
 */
export const notifications = Object.freeze({
  error: (message: string) => publish("error", message),
  info: (message: string) => publish("info", message),
  success: (message: string) => publish("success", message),
});

export function BusinessNotificationProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [queue, setQueue] = useState<BusinessNotification[]>([]);
  const active = queue[0] ?? null;

  useEffect(() => {
    const listener: NotificationListener = (notification) => {
      setQueue((current) => [...current, notification]);
    };
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const dismissActive = () => {
    setQueue((current) => current.slice(1));
  };

  return (
    <>
      {children}
      <Snackbar
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        autoHideDuration={5000}
        key={active?.id ?? "empty"}
        onClose={(_event, reason) => {
          if (reason !== "clickaway") dismissActive();
        }}
        open={active !== null}
      >
        {active ? (
          <Alert
            onClose={dismissActive}
            severity={active.severity}
            variant="filled"
          >
            {active.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}
